import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  HistoricalSelectiveWorkPolicyApplicationDetail,
  WorkPolicyApplicationStage,
  WorkPolicyApplicationValidationIssue
} from "../../../../api/work-policy-application-api.types";
import type { WorkPolicyApplicationDraft } from "./work-policy-application.types";

export type WorkPolicyApplicationUiStatus =
  | "idle"
  | "saving"
  | "draft"
  | "submitted"
  | "stage-failed"
  | "network-unknown"
  | "unsupported-policy"
  | "unsupported-historical"
  | "error";

export interface WorkPolicyApplicationRecovery {
  code: string;
  message: string;
  lastCompletedStage?: WorkPolicyApplicationStage;
  retryable: boolean;
  networkUnknown: boolean;
}

export interface WorkPolicyApplicationController {
  draft: WorkPolicyApplicationDraft | null;
  setDraft: (draft: WorkPolicyApplicationDraft | null) => void;
  unsupportedHistorical: HistoricalSelectiveWorkPolicyApplicationDetail | null;
  setUnsupportedHistorical: (
    detail: HistoricalSelectiveWorkPolicyApplicationDetail | null
  ) => void;
  validationIssues: WorkPolicyApplicationValidationIssue[];
  setValidationIssues: (
    issues: WorkPolicyApplicationValidationIssue[]
  ) => void;
  status: WorkPolicyApplicationUiStatus;
  setStatus: (status: WorkPolicyApplicationUiStatus) => void;
  recovery: WorkPolicyApplicationRecovery | null;
  setRecovery: (recovery: WorkPolicyApplicationRecovery | null) => void;
  approvalRequestIdx?: number;
  setApprovalRequestIdx: (approvalRequestIdx?: number) => void;
  retryCommand?: () => Promise<void>;
  setRetryCommand: (command?: () => Promise<void>) => void;
  isDirty: boolean;
  setIsDirty: (isDirty: boolean) => void;
}

export function useWorkPolicyApplicationController(
  initialApprovalRequestIdx?: number
): WorkPolicyApplicationController {
  const [draft, setDraft] = useState<WorkPolicyApplicationDraft | null>(null);
  const [unsupportedHistorical, setUnsupportedHistorical] =
    useState<HistoricalSelectiveWorkPolicyApplicationDetail | null>(null);
  const [validationIssues, setValidationIssues] = useState<
    WorkPolicyApplicationValidationIssue[]
  >([]);
  const [status, setStatus] = useState<WorkPolicyApplicationUiStatus>("idle");
  const [recovery, setRecovery] =
    useState<WorkPolicyApplicationRecovery | null>(null);
  const [approvalRequestIdx, setApprovalRequestIdx] = useState<number | undefined>(
    initialApprovalRequestIdx
  );
  const [retryCommand, setRetryCommandState] = useState<
    (() => Promise<void>) | undefined
  >();
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (initialApprovalRequestIdx) setApprovalRequestIdx(initialApprovalRequestIdx);
  }, [initialApprovalRequestIdx]);

  const setRetryCommand = useCallback((command?: () => Promise<void>) => {
    setRetryCommandState(() => command);
  }, []);

  return useMemo(
    () => ({
      draft,
      setDraft,
      unsupportedHistorical,
      setUnsupportedHistorical,
      validationIssues,
      setValidationIssues,
      status,
      setStatus,
      recovery,
      setRecovery,
      approvalRequestIdx,
      setApprovalRequestIdx,
      retryCommand,
      setRetryCommand,
      isDirty,
      setIsDirty
    }),
    [
      draft,
      unsupportedHistorical,
      validationIssues,
      status,
      recovery,
      approvalRequestIdx,
      retryCommand,
      setRetryCommand,
      isDirty
    ]
  );
}
